import { defineFilterSchema } from '@/lib/filterSchema'
import type { CardFilters } from '@/types'

/**
 * The single source of truth for the card-browse filter fields that round-trip
 * through the URL. Adding a filter is one line here — the nuqs parsers,
 * URL<->filters mapping, reset payload, and active-count all derive from it
 * (see `@/lib/filterSchema`). Keys are compiler-checked against `CardFilters`.
 *
 * `q`, `sort`, and `order` round-trip but don't count toward the active-filter
 * badge (`active: false`); `sort`/`order` also carry the page defaults.
 *
 * Note: `lifeMin`/`lifeMax` exist on `CardFilters` but are intentionally NOT
 * URL-synced on the cards page (they never were) — don't add them here.
 */
export const CARD_FILTER_SCHEMA = defineFilterSchema<CardFilters>()({
  q: { kind: 'string', active: false },
  color: { kind: 'stringArray' },
  cardType: { kind: 'stringArray' },
  rarity: { kind: 'stringArray' },
  variantType: { kind: 'stringArray' },
  setName: { kind: 'stringArray' },
  attribute: { kind: 'stringArray' },
  subTypes: { kind: 'stringArray' },
  counter: { kind: 'numberArray' },
  trigger: { kind: 'string' },
  costMin: { kind: 'number' },
  costMax: { kind: 'number' },
  powerMin: { kind: 'number' },
  powerMax: { kind: 'number' },
  priceMin: { kind: 'number' },
  priceMax: { kind: 'number' },
  ownership: { kind: 'string' },
  quantity: { kind: 'string' },
  // 'card_image_id' is a wire-literal sort VALUE (matches CARD_COMPARATORS in
  // utils/cardFiltering.ts) — not a field name, so it stays snake_case.
  sort: { kind: 'string', active: false, default: 'card_image_id' },
  order: { kind: 'string', active: false, default: 'asc' },
})
